import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { previewGiftDeliveryFee } from '@shared/api/giftOrder/giftOrder.controller';
import {
  GiftAddressRegion,
  GiftDeliveryFeePreview,
  GiftDeliveryFeePreviewRequest,
} from '@shared/api/giftOrder/giftOrder.interface';

interface Params {
  /** 주문 라인 (giftItem id·수량·옵션) — 수량/옵션이 바뀌면 소계가 바뀌어 무료 기준을 넘나드니 다시 호출 */
  items: GiftDeliveryFeePreviewRequest['orderDetails'];
  /** 카카오 응답 원문. 없으면(구버전 저장 주소) 빈 객체로 보내 서버가 ADDRESS_INCOMPLETE를 돌려주게 한다 */
  region: GiftAddressRegion | undefined;
  /** 배송형 상품 + 일괄배송 + 주소 입력 완료일 때만 */
  enabled: boolean;
}

export interface GiftDeliveryFeePreviewState {
  /** 마지막 응답. enabled=false거나 아직 응답 전이면 null */
  preview: GiftDeliveryFeePreview | null;
  loading: boolean;
  /** 네트워크/서버 오류 (404=구서버 포함). 결제는 막지 않고 서버 검증에 맡긴다 */
  error: boolean;
  /**
   * 미리보기 API 자체가 없는 구서버(404). 이 엔드포인트는 400만 던지므로 404는 라우트 부재로만 본다.
   * true면 재시도해도 같은 결과라 "다시 확인" 같은 재시도 UI를 숨긴다
   */
  unsupported: boolean;
  /** 결제금액에 더할 배송비 — 배송 가능일 때만 total, 그 외 0 */
  feeTotal: number;
  /** 권역 배송비 대상 주문인데 지금 상태로는 결제할 수 없음 (배송 불가·주소 불완전·확인 중) */
  blocksPayment: boolean;
  /** 수동 재조회 — 네트워크/5xx 오류 뒤 "다시 확인" 용. applicable=false 캐시도 함께 비운다 */
  refresh: () => void;
}

const DEBOUNCE_MS = 300;

/**
 * 권역 배송비 미리보기 — 주소 선택 직후·수량 변경 시 서버에 물어 배송비/배송가능 여부를 받는다.
 * - 300ms 디바운스, 늦게 온 응답은 버림(요청 순번)
 * - 같은 상품 집합이 한 번 applicable=false(권역 브랜드 아님)로 판정되면 그 집합에서는 다시 묻지 않는다
 */
export const useGiftDeliveryFeePreview = ({
  items,
  region,
  enabled,
}: Params): GiftDeliveryFeePreviewState => {
  const [preview, setPreview] = useState<GiftDeliveryFeePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [unsupported, setUnsupported] = useState(false);
  const [tick, setTick] = useState(0);
  const seqRef = useRef(0);
  const notApplicableKeyRef = useRef<string | null>(null);

  // 요청 본문을 문자열 키로 — 객체 참조가 바뀌어도 내용이 같으면 재호출하지 않는다
  const itemsKey = useMemo(
    () =>
      JSON.stringify(
        items.map((it) => ({
          giftItem: it.giftItem,
          quantity: it.quantity,
          selectedOptions: (it.selectedOptions ?? []).map((o) => ({
            optionName: o.optionName,
            slotIndex: o.slotIndex,
            choiceName: o.choiceName,
            priceDelta: o.priceDelta,
          })),
        })),
      ),
    [items],
  );
  const itemSetKey = useMemo(
    () =>
      items
        .map((it) => it.giftItem)
        .sort()
        .join(','),
    [items],
  );
  const regionKey = useMemo(() => JSON.stringify(region ?? {}), [region]);

  useEffect(() => {
    if (!enabled || items.length === 0) {
      seqRef.current += 1;
      setPreview(null);
      setLoading(false);
      setError(false);
      setUnsupported(false);
      return;
    }
    if (notApplicableKeyRef.current === itemSetKey) {
      setPreview({ applicable: false });
      setLoading(false);
      setError(false);
      setUnsupported(false);
      return;
    }

    const seq = ++seqRef.current;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const body: GiftDeliveryFeePreviewRequest = {
          orderDetails: JSON.parse(itemsKey),
          address: JSON.parse(regionKey),
        };
        const result = await previewGiftDeliveryFee(body);
        if (seq !== seqRef.current) return; // 늦게 온 응답
        if (!result.applicable) notApplicableKeyRef.current = itemSetKey;
        setPreview(result);
        setError(false);
        setUnsupported(false);
      } catch (err) {
        if (seq !== seqRef.current) return;
        console.error(err);
        setPreview(null);
        setError(true);
        setUnsupported(
          (err as { response?: { status?: number } })?.response?.status === 404,
        );
      } finally {
        if (seq === seqRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, itemsKey, itemSetKey, regionKey, tick]);

  const refresh = useCallback(() => {
    notApplicableKeyRef.current = null;
    setTick((t) => t + 1);
  }, []);

  const feeTotal = preview?.applicable && preview.deliverable ? preview.total : 0;
  // 확인 중에도 막는다 — 수량을 바꾼 직후 옛 배송비로 결제되는 것을 방지
  const blocksPayment =
    enabled && (loading || (preview?.applicable === true && !preview.deliverable));

  return { preview, loading, error, unsupported, feeTotal, blocksPayment, refresh };
};
